import { gql } from '@apollo/client';
import type React from 'react';
import type {
  OtherSideEffectOption,
  PresetSideEffectOption,
  SideEffect,
  SideEffectDuration,
  SideEffectPersistence,
  SideEffectSeverity,
  SideEffectTrackingFragment,
} from 'graphql/types';
import { FaCheckSquare, FaRegSquare } from 'react-icons/fa';
import { Accordion } from 'components/accordion';
import { formatDate } from 'utils/misc';

const SelectedResponse = ({ value }: { value?: string }): JSX.Element => (
  <div className="flex items-center text-slate-800">
    <FaCheckSquare className="text-blue-500" />
    <span className="ml-2">{value}</span>
  </div>
);

const UnselectedResponse = ({ value }: { value?: string }): JSX.Element => (
  <div className="flex items-center text-slate-500">
    <FaRegSquare />
    <span className="ml-2">{value}</span>
  </div>
);

const renderSideEffects = (
  reportedSideEffects: Array<OtherSideEffectOption | PresetSideEffectOption>,
) => {
  const sideEffectOptions: Array<{ label: string; value: SideEffect }> = [
    {
      label: 'Nausea',
      value: 'NAUSEA',
    },
    {
      label: 'Vomiting',
      value: 'VOMITING',
    },
    {
      label: 'Diarrhoea',
      value: 'DIARRHEA',
    },
    {
      label: 'Constipation',
      value: 'CONSTIPATION',
    },
    {
      label: 'Headaches',
      value: 'HEADACHES',
    },
    {
      label: 'Fatigue',
      value: 'FATIGUE',
    },
    {
      label: 'Reflux',
      value: 'REFLUX',
    },
    {
      label: 'Injection site irritation',
      value: 'INJECTION_SITE_IRRITATION',
    },
  ];

  const otherAnswers = reportedSideEffects
    .filter(
      (rse): rse is OtherSideEffectOption =>
        rse.__typename === 'OtherSideEffectOption',
    )
    .map((rse) => rse.other);

  const presetAnswers = reportedSideEffects
    .filter(
      (rse): rse is PresetSideEffectOption =>
        rse.__typename === 'PresetSideEffectOption',
    )
    .map((rse) => rse.preset);

  return (
    <>
      <div className="font-semibold py-1">
        <p>Have you experienced any side effects this week?</p>
      </div>
      <div>
        {sideEffectOptions.map((option) => (
          <div key={option.value}>
            {presetAnswers.includes(option.value) ? (
              <div>
                <SelectedResponse value={option.label || undefined} />
              </div>
            ) : (
              <UnselectedResponse value={option.label || undefined} />
            )}
          </div>
        ))}
        {otherAnswers.map((answer) => (
          <p key={answer}>
            <span className="font-semibold py-1">Other:</span> {answer}
          </p>
        ))}
      </div>
    </>
  );
};

const renderSeverity = (severity: SideEffectSeverity) => {
  const options = [
    {
      label: 'Mild',
      value: 'MILD',
    },
    {
      label: 'Moderate',
      value: 'MODERATE',
    },
    {
      label: 'Severe',
      value: 'SEVERE',
    },
  ];

  return (
    <div>
      <p className="font-semibold py-1">How would you rate their severity?</p>
      <div>
        {options.map((option) => (
          <div key={option.value}>
            {severity === option.value ? (
              <SelectedResponse value={option.label} />
            ) : (
              <UnselectedResponse value={option.label} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const renderPersistence = (persistence: SideEffectPersistence) => {
  const options = [
    {
      label: 'Not persisting',
      value: 'NOT_PERSISTING',
    },
    {
      label: 'Persisting today',
      value: 'PERSISTING_TODAY',
    },
  ];

  return (
    <div>
      <p className="font-semibold py-1">
        Are your side effects persisting today?
      </p>
      {options.map((option) => (
        <div key={option.value}>
          {option.value === persistence ? (
            <SelectedResponse value={option.label} />
          ) : (
            <UnselectedResponse value={option.label} />
          )}
        </div>
      ))}
    </div>
  );
};

const renderDuration = (duration: SideEffectDuration) => {
  const options = [
    {
      label: 'Less than 24 hours',
      value: 'LESS_THAN_24',
    },
    {
      label: 'Between 24 and 48 hours',
      value: 'BETWEEN_24_48',
    },
    {
      label: 'More than 48 hours',
      value: 'MORE_THAN_48',
    },
  ];
  return (
    <div>
      <p className="font-semibold py-1">
        How long have you been experiencing these side effects?
      </p>
      <div>
        {options.map((option) => (
          <div key={option.value}>
            {duration === option.value ? (
              <SelectedResponse value={option.label} />
            ) : (
              <UnselectedResponse value={option.label} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const renderPriorSelfTreatmentNotes = (priorSelfTreatmentNotes: string) => (
  <div>
    <p className="font-semibold py-1">
      What (if anything) have you tried so far to reduce your side effects?
    </p>
    <p>{priorSelfTreatmentNotes}</p>
  </div>
);

const renderPatientMessage = (patientMessage: string) => (
  <div>
    <p className="font-semibold py-1">Tell us more about your side effects</p>
    <p>{patientMessage}</p>
  </div>
);

const SideEffectTracking = ({
  data,
}: {
  data: SideEffectTrackingFragment | null;
}): React.ReactElement => {
  if (!data?.sideEffectReports || data.sideEffectReports.length === 0) {
    return (
      <div>
        <p>Patient has not reported any side effects</p>
      </div>
    );
  }

  return (
    <div className="pb-3 flex flex-col">
      {data.sideEffectReports?.map((ser, idx) => (
        <div className="bg-white mb-4" key={ser.id}>
          <Accordion
            title={formatDate(new Date(ser.submittedAt))}
            tags={[{ text: 'Side Effect Check In' }]}
            startOpen={idx === 0}
          >
            <div>
              <div className="px-4 pb-2 even:bg-slate-200">
                {renderSideEffects(ser.reportedSideEffects ?? [])}
              </div>
              {ser.severity && (
                <div className="px-4 pb-2 even:bg-slate-200">
                  {renderSeverity(ser.severity)}
                </div>
              )}
              {ser.persistence && (
                <div className="px-4 pb-2 even:bg-slate-200">
                  {renderPersistence(ser.persistence)}
                </div>
              )}
              {ser.duration && (
                <div className="px-4 pb-2 even:bg-slate-200">
                  {renderDuration(ser.duration)}
                </div>
              )}
              {ser.priorSelfTreatmentNotes && (
                <div className="px-4 pb-2 even:bg-slate-200">
                  {renderPriorSelfTreatmentNotes(ser.priorSelfTreatmentNotes)}
                </div>
              )}
              {ser.patientMessage && (
                <div className="px-4 pb-2 even:bg-slate-200">
                  {renderPatientMessage(ser.patientMessage)}
                </div>
              )}
            </div>
          </Accordion>
        </div>
      ))}
    </div>
  );
};

SideEffectTracking.fragment = gql`
  fragment SideEffectTracking on User {
    sideEffectReports {
      id
      severity
      duration
      persistence
      reportedSideEffects {
        __typename
        id
        ... on PresetSideEffectOption {
          id
          preset
        }
        ... on OtherSideEffectOption {
          id
          other
        }
      }
      patientMessage
      priorSelfTreatmentNotes
      submittedAt
    }
  }
`;

export { SideEffectTracking };
